import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
    fetchIntegrations,
    type IntegrationTypeDefinition,
    type DynamicField,
} from '@/features/adp/api/integrations.api'

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Maps the connectorId used in the UI/routing to the API's `name` field.
 * For integrations: join, union
 */
export const INTEGRATION_CONNECTOR_TO_API_NAME: Record<string, string> = {
    join: 'join',
    union: 'union',
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type UseIntegrationsResult = {
    /** The matched integration definition for the given connectorId, or null if not found / still loading. */
    selectedIntegrationType: IntegrationTypeDefinition | null
    /** All fields from the matched integration's schema definition. Empty array if none. */
    fields: DynamicField[]
    /** Whether the API request is in-flight. */
    isLoading: boolean
    /** Error message string if the fetch failed, otherwise null. */
    error: string | null
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Optional reverse map of API integration names to UI connector ids.
 * Keep this in sync with `INTEGRATION_CONNECTOR_TO_API_NAME`.
 */
const INTEGRATION_API_NAME_TO_CONNECTOR: Record<string, string> = Object.entries(
    INTEGRATION_CONNECTOR_TO_API_NAME
).reduce((acc, [connectorId, apiName]) => {
    acc[apiName.trim().toLowerCase()] = connectorId
    return acc
}, {} as Record<string, string>)

function normalizeIntegrationKey(value: string): string {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function findIntegrationTypeByConnector(
    integrationTypes: IntegrationTypeDefinition[],
    connectorId: string
): IntegrationTypeDefinition | null {
    const normalizedConnector = normalizeIntegrationKey(connectorId)

    const expectedApiName =
        INTEGRATION_CONNECTOR_TO_API_NAME[connectorId.trim().toLowerCase()] ??
        INTEGRATION_API_NAME_TO_CONNECTOR[connectorId.trim().toLowerCase()]

    return (
        integrationTypes.find(item => {
            const normalizedName = normalizeIntegrationKey(item.name ?? '')

            if (expectedApiName) {
                return (
                    normalizedName === normalizeIntegrationKey(expectedApiName) ||
                    normalizedName.includes(normalizeIntegrationKey(expectedApiName)) ||
                    normalizedConnector === normalizeIntegrationKey(item.name)
                )
            }

            return (
                normalizedName === normalizedConnector ||
                normalizedName.includes(normalizedConnector) ||
                normalizedConnector.includes(normalizedName)
            )
        }) ?? null
    )
}

export function useAllIntegrations() {
    const { data = [], isLoading, error } = useQuery<IntegrationTypeDefinition[], Error>({
        queryKey: ['adp', 'integrations'],
        queryFn: () => fetchIntegrations(),
        staleTime: 60_000,
    })

    return {
        integrations: Array.isArray(data) ? data : [],
        isLoading,
        error: error?.message ?? null,
    }
}

/**
 * Fetches all integration types (join, union) from the API and resolves the one
 * matching the given `connectorId` via `INTEGRATION_CONNECTOR_TO_API_NAME`.
 */
export function useIntegrations(
    connectorId: string | undefined
): UseIntegrationsResult {
    const { integrations, isLoading, error } = useAllIntegrations()

    const selectedIntegrationType = useMemo(() => {
        if (!connectorId || !integrations.length) return null

        return findIntegrationTypeByConnector(integrations, connectorId)
    }, [integrations, connectorId])

    const fields = selectedIntegrationType?.schema_definition?.fields ?? []

    return {
        selectedIntegrationType,
        fields,
        isLoading,
        error,
    }
}

// ─── Helpers exported for use in JoinDetailsSheet ─────────────────────────────

/**
 * Builds an empty form state from a list of DynamicFields.
 * Multiselect fields default to `[]`, groups with multiple default to `[]`, groups without multiple default to an object with nested fields.
 */
export function buildInitialFormState(
    fields: DynamicField[]
): Record<string, any> {
    const state: Record<string, any> = {}
    for (const field of fields) {
        if (field.type === 'multiselect') {
            state[field.key] = []
        } else if (field.type === 'group') {
            if (field.multiple || field.repeatable) {
                state[field.key] = []
            } else {
                state[field.key] = buildInitialFormState(field.fields || [])
            }
        } else {
            state[field.key] = ''
        }
    }
    return state
}
